import { useEffect, useMemo, useState } from 'react';
import { Ban, Loader2, Phone, Plus, Save, Trash2, UserRound, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  fetchPowerPlantContactCards,
  savePowerPlantContactCards,
  type PowerPlantContactCardEntry,
  type PowerPlantContactCardGroups,
} from '@/api/powerplants';

const GROUPS = [
  { key: 'responsible', label: 'Sorumlu' },
  { key: 'security', label: 'Bekçi' },
  { key: 'technical', label: 'Teknik Personel' },
  { key: 'finance', label: 'Mali İşler' },
  { key: 'administrative', label: 'İdari İşler' },
] as const;

const EMPTY_ROW: PowerPlantContactCardEntry = { name: '', phone: '' };

function normalizeGroups(groups: PowerPlantContactCardGroups): PowerPlantContactCardGroups {
  const normalized: PowerPlantContactCardGroups = {};

  for (const group of GROUPS) {
    const rows = Array.isArray(groups[group.key]) ? groups[group.key] : [];
    normalized[group.key] = rows.length ? rows : [{ ...EMPTY_ROW }];
  }

  return normalized;
}

export default function PlantContactCardsModal({
  uuid_id,
  onClose,
}: {
  uuid_id: string;
  onClose: () => void;
}) {
  const [groups, setGroups] = useState<PowerPlantContactCardGroups>(() => normalizeGroups({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    void (async () => {
      setLoading(true);
      try {
        const response = await fetchPowerPlantContactCards(uuid_id);
        if (!mounted) return;
        setGroups(normalizeGroups(response));
      } catch (error) {
        if (!mounted) return;
        toast.error(error instanceof Error ? error.message : 'İletişim kartı yüklenemedi.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [uuid_id]);

  const preparedPayload = useMemo(() => {
    const payload: PowerPlantContactCardGroups = {};

    for (const group of GROUPS) {
      payload[group.key] = (groups[group.key] ?? [])
        .map((entry) => ({
          name: entry.name.trim(),
          phone: entry.phone.trim(),
        }))
        .filter((entry) => entry.name || entry.phone);
    }

    return payload;
  }, [groups]);

  const updateRow = (groupKey: string, index: number, field: keyof PowerPlantContactCardEntry, value: string) => {
    setGroups((current) => ({
      ...current,
      [groupKey]: (current[groupKey] ?? []).map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const addRow = (groupKey: string) => {
    setGroups((current) => ({
      ...current,
      [groupKey]: [...(current[groupKey] ?? []), { ...EMPTY_ROW }],
    }));
  };

  const removeRow = (groupKey: string, index: number) => {
    setGroups((current) => {
      const nextRows = (current[groupKey] ?? []).filter((_, rowIndex) => rowIndex !== index);
      return {
        ...current,
        [groupKey]: nextRows.length ? nextRows : [{ ...EMPTY_ROW }],
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await savePowerPlantContactCards(uuid_id, preparedPayload);
      toast.success('İletişim kartı kaydedildi.');
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'İletişim kartı kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/60 px-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-[16px] bg-white shadow-2xl ring-1 ring-black/5">
        <div className="flex items-start justify-between border-b border-gray-100 bg-white px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">İletişim Kartı</h2>
            <p className="mt-1 text-sm text-gray-500">Saha ile ilgili iletişime geçilebilecek kişileri grup bazında yönet.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-1 rounded-lg p-1.5 text-gray-400 transition hover:bg-rose-50 hover:text-rose-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            İletişim kartı yükleniyor...
          </div>
        ) : (
          <div className="grid gap-4 overflow-y-auto bg-slate-50/30 px-6 py-5 md:grid-cols-2 xl:grid-cols-3">
            {GROUPS.map((group) => (
              <section key={group.key} className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">{group.label}</h3>
                    <p className="mt-0.5 text-xs text-gray-500">Birden fazla kişi ekleyebilirsin.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => addRow(group.key)}
                    className="flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-emerald-700 transition hover:bg-emerald-50"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Yeni
                  </button>
                </div>

                <div className="space-y-2.5">
                  {(groups[group.key] ?? []).map((entry, index) => (
                    <div key={`${group.key}-${index}`} className="rounded-xl border border-gray-200 bg-gray-50/35 p-3">
                      <div className="grid gap-2">
                        <label className="space-y-1">
                          <span className="text-xs font-medium text-gray-600">İsim</span>
                          <div className="group relative">
                            <UserRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                            <input
                              value={entry.name}
                              onChange={(e) => updateRow(group.key, index, 'name', e.target.value)}
                              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              placeholder="Ad Soyad"
                            />
                          </div>
                        </label>

                        <label className="space-y-1">
                          <span className="text-xs font-medium text-gray-600">Telefon</span>
                          <div className="group relative">
                            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-blue-500" />
                            <input
                              value={entry.phone}
                              onChange={(e) => updateRow(group.key, index, 'phone', e.target.value)}
                              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              placeholder="05xx xxx xx xx"
                            />
                          </div>
                        </label>
                      </div>

                      <div className="mt-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeRow(group.key, index)}
                          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-rose-600 transition hover:bg-rose-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Sil
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="flex w-32 items-center justify-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 transition hover:bg-gray-50"
          >
            <Ban className="h-4 w-4 text-gray-500" />
            Vazgeç
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex w-32 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}
